

















































import { Component, Vue } from 'vue-property-decorator';
import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import proj4 from 'proj4';

@Component
export default class MeasureTool extends Vue {
    public showMeasureToolbar: boolean = false;
    public showArea: boolean = false;
    public isImperial: boolean = false;
    public currentMeasurement: number = 0;
    public currentArea: number = 0;
    public isUndoDisabled: boolean = true;
    public drawLine: any = null;
    public coords: any = [];
    public projectedCoords: any = [];
    public drawLayer: L.GeoJSON | null = null;
    public areaOverlayLayer: L.GeoJSON | null = null;
    public drawLineStyle = {
        color: '#DC1C2E',
        weight: 4,
        opacity: 1,
    };
    public drawPolygonStyle = {
        color: '#000000',
        fillColor: '#000000',
        weight: 0,
        opacity: 1,
        fillOpacity: 0.3,
    };

    get formattedMeasurement() {
        if (!this.isImperial) {
            if (this.currentMeasurement >= 1000) {
                return `${Math.round((this.currentMeasurement / 1000) * 100) / 100} km`;
            }
            return `${Math.round(this.currentMeasurement * 100) / 100} m`;
        }
        return `${Math.round(this.currentMeasurement * 100 * 3.28084) / 100} ft`;
    }

    get formattedArea() {
        if (!this.isImperial) {
            if (this.currentArea >= 1000000) {
                return `${Math.round((this.currentArea / 1000000) * 100) / 100} km`;
            }
            return `${Math.round(this.currentArea * 100) / 100} m`;
        }
        if (this.currentArea * 0.000247105 >= 1.0) {
            return `${Math.round(this.currentArea * 0.000247105 * 100) / 100} acres`;
        }
        return `${Math.round(this.currentArea * 10.7639 * 100) / 100} ft`;
    }

    get map() {
        return this.$store.state.map;
    }

    public onToggleMeasure() {
        console.log('onToggleMeasure');
        this.showMeasureToolbar = !this.showMeasureToolbar;
        this.showMeasureToolbar ? this.onInitMeasure() : this.onCancelMeasure();
    }

    public onInitMeasure() {
        this.$store.dispatch('disableMapClick');
        this.showArea = false;
        this.isImperial = false;

        // @ts-ignore - no typedefs
        L.drawLocal.draw.handlers.polyline.tooltip.start = 'Click to start drawing a line';
        // @ts-ignore - no typedefs
        L.drawLocal.draw.handlers.polyline.tooltip.cont = '';
        // @ts-ignore - no typedefs
        L.drawLocal.draw.handlers.polyline.tooltip.end = '';
        // @ts-ignore - no typedefs
        this.drawLine = new L.Draw.Polyline(this.map, {
            showLength: false,
            allowIntersection: true,
            drawError: {
                color: '#ffa500',
                message: '<strong>Opps...sorry!<strong> you can\'t draw that!',
            },
            shapeOptions: {
                color: '#0043ff',
                opacity: 1.0,
                weight: 6,
            },
        });
        this.drawLine.enable();

        // @ts-ignore
        proj4.defs('EPSG:2961', '+proj=utm +zone=20 +ellps=GRS80 +units=m +no_defs');

        let firstMarker = null;
        // @ts-ignore - no typedefs
        this.map.on(L.Draw.Event.DRAWVERTEX, (e) => {
            if (e.layers.getLayers().length === 1) {
                firstMarker = e.layers.getLayers()[0];
                firstMarker.on('click', (v: any) => {
                    this.drawLine.addVertex(v.latlng);
                    this.drawLine.completeShape();
                });
            }
            this.coords = [];
            this.projectedCoords = [];
            for (const feature of e.layers.toGeoJSON().features) {
                this.coords.push([feature.geometry.coordinates[1], feature.geometry.coordinates[0]]);
                const projected = proj4('EPSG:2961', feature.geometry.coordinates);
                this.projectedCoords.push(projected);
            }
            this.recalcDistance();
            if (this.projectedCoords.length > 1) {
                this.isUndoDisabled = false;
            }
            this.recalcArea();
            if (this.showArea) {
                this.areaOverlayLayer?.removeFrom(this.map);
                const poly  = L.polygon(this.coords);
                this.areaOverlayLayer = L.geoJSON(poly.toGeoJSON(),
                    {
                        style: this.drawPolygonStyle,
                    });
                this.areaOverlayLayer.addTo(this.map);
            }
        });

        // @ts-ignore - no typedefs
        this.map.on(L.Draw.Event.CREATED, (e) => {
            // drawstop gets fired when created so disable it!
            this.map.off('draw:drawstop');
            this.drawLayer = L.geoJSON(e.layer.toGeoJSON(),
                {
                    style: this.drawLineStyle,
                });

            this.map.removeLayer(e.layer);
            this.drawLayer.addTo(this.map);
        });

        // @ts-ignore - no typedefs
        this.map.on('draw:drawstop', (e) => {
            this.onCancelMeasure();
        });
    }

    public recalcArea() {
        if (this.coords.length > 2) {
            // Compare with turf calculation
            // const polygon = turf.polygon([ this.coords.concat([this.coords[0]]) ]); // turf wants a closed polygon
            // const a: any = area(polygon);
            // console.log(a);
            let a2 = 0;
            for (let i = 0, l = this.projectedCoords.length; i < l; i++) {
                const addX = this.projectedCoords[i][0];
                const addY = this.projectedCoords[i === this.projectedCoords.length - 1 ? 0 : i + 1][1];
                const subX = this.projectedCoords[i === this.projectedCoords.length - 1 ? 0 : i + 1][0];
                const subY = this.projectedCoords[i][1];
                a2 += (addX * addY * 0.5);
                a2 -= (subX * subY * 0.5);
            }
            this.currentArea = Math.abs(a2);
        } else {
            this.currentArea = 0;
        }
    }

    public recalcDistance() {
        if (this.projectedCoords.length > 1) {
            let sum = 0;
            for (let i = 0; i < this.projectedCoords.length - 1; i++) {
                sum += this.utmDistance(this.projectedCoords[i][0], this.projectedCoords[i][1], this.projectedCoords[i + 1][0], this.projectedCoords[i + 1][1]);
            }
            this.currentMeasurement = sum;
        } else {
            this.currentMeasurement = 0;
        }
    }

    public utmDistance(x1: number, y1: number, x2: number, y2: number) {
        const xDiff = Math.abs(x1 - x2);
        const yDiff = Math.abs(y1 - y2);
        return Math.sqrt((xDiff * xDiff) + (yDiff * yDiff));
    }

    public onToggleMeasureType() {
        console.log('onToggleMeasureType');
        if (this.showArea) {
            const poly  = L.polygon(this.coords);
            this.areaOverlayLayer = L.geoJSON(poly.toGeoJSON(),
                {
                    style: this.drawPolygonStyle,
                });
            this.areaOverlayLayer.addTo(this.map);
        } else {
            this.areaOverlayLayer?.removeFrom(this.map);
        }
    }

    public onUndo() {
        if (this.drawLayer) {
            this.map.removeLayer(this.drawLayer);
            this.drawLayer = null;
            this.areaOverlayLayer?.removeFrom(this.map);
            this.areaOverlayLayer = null;
            this.currentMeasurement = 0;
            this.currentArea = 0;
            // @ts-ignore
            this.map.off(L.Draw.Event.CREATED);
            // @ts-ignore
            this.map.off(L.Draw.Event.DRAWVERTEX);
            this.map.off('draw:drawstop');
            this.onInitMeasure();
        } else {
            if (this.drawLine) {
                this.drawLine.deleteLastVertex();
            }
        }
    }

    public onCancelMeasure() {
        console.log('MeasureTool: onCancelMeasure()');
        this.$store.dispatch('enableMapClick');
        if (this.drawLine) {
            // @ts-ignore
            this.map.off(L.Draw.Event.CREATED);
            // @ts-ignore
            this.map.off(L.Draw.Event.DRAWVERTEX);
            this.map.off('draw:drawstop');
            this.drawLine.disable();
            this.drawLine = null;
        }
        if (this.drawLayer) {
            this.map.removeLayer(this.drawLayer);
            this.drawLayer = null;
        }
        if (this.areaOverlayLayer) {
            this.map.removeLayer(this.areaOverlayLayer);
            this.areaOverlayLayer = null;
        }
        this.showMeasureToolbar = false;
        this.currentMeasurement = 0;
    }
}

